import { hashKey, type Query, QueryClient, type QueryClientConfig } from '@tanstack/react-query';
import { axiosInstance, type ResponseError } from 'app/axios';
import axios, { type AxiosError } from 'axios';

const CACHE_TIME = 1000 * 60 * 60 * 24; // 24 hours
const STALE_TIME = 5000; // 5 seconds
const PREVENT_QUERY_RETRY_STATUS_CODES = [400, 401, 403, 404];
const MAX_RETRY_COUNT = 3;

const isNetworkError = (error: AxiosError) => error.request.status === 0;
const hasCachedData = (query: Query) => query.state.data !== undefined;

const isRetryableQueryError = (error: ResponseError) => {
  const status = error.response?.status;
  return !PREVENT_QUERY_RETRY_STATUS_CODES.includes(status!);
};

// Kubb-generated regional query keys start with `{ url, ... }`; global keys
// start with the literal 'global' and app-local keys with other values.
const isRegionalQueryKey = (queryKey: ReadonlyArray<unknown>) =>
  typeof queryKey[0] === 'object' && queryKey[0] !== null && 'url' in queryKey[0];

// Keys that already carry a `{ region }` segment (the fan-out keys and any query
// issued with an explicit per-request baseURL) are region-scoped by construction.
// Prefixing them with the mutable defaults.baseURL would change their hash on
// every project navigation, silently orphaning cached data (e.g. the always-
// mounted ProjectsMenu would refetch every region's project list on each switch).
const isRegionScopedQueryKey = (queryKey: ReadonlyArray<unknown>) =>
  queryKey.some((segment) => typeof segment === 'object' && segment !== null && 'region' in segment);

// Regional query keys exclude the axios host, so the same key would share one
// cache slot across regions — a project fetched against the wrong region caches
// a 404 that `retryOnMount: false` then serves on every mount (SE-2265). Hashing
// the active baseURL into regional keys gives each region its own slot. Global,
// app-local, and explicitly region-scoped keys are stable and keep the default hash.
export const regionAwareQueryKeyHashFn = (queryKey: ReadonlyArray<unknown>): string =>
  isRegionalQueryKey(queryKey) && !isRegionScopedQueryKey(queryKey)
    ? hashKey([axiosInstance.defaults.baseURL, ...queryKey])
    : hashKey(queryKey);

export const defaultQueryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      queryKeyHashFn: regionAwareQueryKeyHashFn,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      networkMode: 'online',
      gcTime: CACHE_TIME,
      staleTime: STALE_TIME,
      retryOnMount: false,
      retry: (failureCount, error) => {
        if (axios.isAxiosError(error) && failureCount < MAX_RETRY_COUNT) {
          if (isRetryableQueryError(error)) return true;

          return isNetworkError(error);
        }

        return false;
      },
      throwOnError: (error, query) => {
        if (axios.isAxiosError(error)) {
          if (isNetworkError(error) && hasCachedData(query)) return false;
        }
        /**
         * We need to rely on options.suspense because react-query does not care about it if useErrorBoundary is defined.
         * However, we only want to affect queries performed with suspense: true; otherwise, we want to fallback on
         * library behaviour.
         */
        // @ts-expect-error Property 'suspense' does not exist on type 'QueryOptions<unknown, unknown, unknown, QueryKey>'.ts(2339)
        return Boolean(query.options.suspense);
      },
    },
  },
};

export const buildQueryClient = (config: QueryClientConfig = defaultQueryClientConfig) => new QueryClient(config);

const queryClient = buildQueryClient();

export default queryClient;
